/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'

import { DELETE_DAI_GROUP, GET_TENANT_GROUPS } from '@/graphql'
import useT from '@/i18n/useT'

interface Props {
  open: boolean
  onClose: () => void
  groupId: string
  groupName: string
  onSuccess?: () => void
}

export const DeleteRoleDialog: React.FC<Props> = ({
  open,
  onClose,
  groupId,
  groupName,
  onSuccess,
}) => {
  const { t } = useT()

  const [deleteDaiGroup, { loading, error }] = useMutation(DELETE_DAI_GROUP, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_TENANT_GROUPS }],
  })

  const handleDelete = async () => {
    try {
      await deleteDaiGroup({ variables: { id: groupId } })
      onSuccess?.()
      onClose()
    } catch {
      // Apollo sets `error`
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('manageRoles.deleteTitle', 'Delete Role')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}
        <Typography>
          {t(
            'manageRoles.deleteConfirmMessage',
            'Are you sure you want to delete this role? This action cannot be undone.',
          )}
        </Typography>
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle1">{groupName}</Typography>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={loading}
          loading={loading}
        >
          {t('common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
